import axios from 'axios';
import getActionCable, { createWebsocketListener } from '../../../utils/actioncable';
import { debounce } from '../../utils/general';
import * as actions from './actions';
import { dashboardAuthHeader } from '../dashboards/selectors';
import batchRequests from '../../../utils/batch-requests';
import { API_PATH } from '../../../constants';
import { roundTimeDown } from '../../../utils/time';
import { overlappingIntervals } from '../../../utils/schedules';
import { transientSchedules } from './selectors';
import moment from 'moment';

const batchedUpdates = [];

let dispatch = null;
// To avoid a lot of updates, we debounce all information we receive from the
// websocket. When it's "silent" for 500ms, a state update is performed
const batchUpdaterWebsockets = debounce(() => {
  dispatch(
    actions.batchCombinedScheduleUpdated(batchedUpdates, {
      overwriteUnsavedChanges: false
    })
  );
  batchedUpdates.length = 0;
}, 250);

const handleReceivedWebsocketMessage = schedule => {
  batchedUpdates.push(schedule);
  batchUpdaterWebsockets();
};

// Plain GET operation for individual combined schedules of a single entity
export const fetchObjects =
  ({ id, type, start_time, end_time }) =>
  dispatch => {
    dispatch(actions.fetchCombinedScheduleStart());
    const params = { start_time, end_time };
    const url = `${API_PATH}/${type}s/${id}/combined_schedule`;
    return axios.get(url, { params }).then(response => {
      dispatch(actions.combinedScheduleUpdated(response.data.owner_id, response.data.owner_type, response.data));
    });
  };

// Method for fetching multiple combined schedules from the API.
export const fetchMultiple = batchRequests({
  batchSize: 20,
  startLoadingCallback: actions.fetchCombinedScheduleStart(),
  finishLoadingCallback: actions.fetchCombinedScheduleSuccess(),
  executor:
    ({ ids, type, look_ahead, start_time }) =>
    (d, getState) => {
      // team should use the groups API
      if (type === 'team') type = 'group';

      const pluralizedType = `${type}s`;
      const startTime = roundTimeDown(moment(start_time) || moment());
      const params = {
        group_ids: ids.join(','),
        start_time: startTime.format()
      };
      if (look_ahead) params.end_time = startTime.add(look_ahead, 'seconds').format();
      const url = `/api/v2/${pluralizedType}/combined_schedules`;
      const headers = dashboardAuthHeader(getState());

      const dispatch = d;

      return axios.get(url, { params, headers }).then(response => {
        const schedules = response.data;
        // handle the schedules in *merge* mode
        dispatch(actions.batchCombinedScheduleUpdated(schedules));
      });
    }
});

// Utility method used to split a time period into a set of
// smaller chunks (of 5 days). This is necessary because
// Websockets and the API have a LOOK_AHEAD cap of 5 days
const chopPeriod = (startTime, lookAhead) => {
  if (!lookAhead) return [{ startTime, lookAhead }];

  const MAX_CHUNK_SIZE = 432_000; // 5 days in seconds

  // First calculate the number of chunks that fit
  const chunkCount = Math.floor(lookAhead / MAX_CHUNK_SIZE);

  let currentStart = moment(startTime);
  // Create an array with the number of full chunks that fit
  let chunkSizes = Array.from({ length: chunkCount }).fill(MAX_CHUNK_SIZE);
  // If there is a remainder, add it too
  let chunkTail = lookAhead % MAX_CHUNK_SIZE;
  if (chunkTail > 0) chunkSizes.push(chunkTail);

  // Create time chunks with the form { start_time: ..., lookAhead: ... }
  // These will be consumed by the registerForCombinedScheduleUpdates
  return chunkSizes.map(chunkSize => {
    const newChunk = { startTime: currentStart.clone(), lookAhead: chunkSize };
    currentStart.add(5, 'days');
    return newChunk;
  });
};

// bound actions
export const registerForCombinedScheduleUpdates =
  ({ groupIds, type, look_ahead, start_time }, actionCable) =>
  d => {
    dispatch = d;

    const initialLoad = () => {
      // do an API call to get information about all the combined schedules that
      // are registered to for every time chunk. This is the baseline information
      // load. Websocket real time updates will only apply for the first time chunk
      for (const chunk of chopPeriod(start_time, look_ahead))
        dispatch(
          fetchMultiple({
            ids: groupIds,
            type,
            look_ahead: chunk.lookAhead,
            start_time: chunk.startTime
          })
        );
    };

    const subscribeToWebsocket = () => {
      const connectHandler = () =>
        console.log(
          `Connected to CombinedSchedulesNotificationsChannel for \
        ${type} ${groupIds} for time ${start_time} and lookahead ${look_ahead}`
        );

      const listener = createWebsocketListener({
        dispatch,
        connectHandler,
        receivedHandler: handleReceivedWebsocketMessage,
        fallbackPoller: null, // Poller disabled; restore with initialLoad
        fallbackPollerInterval: 30 * 60 * 1000
      });

      const channelData = {
        channel: 'CombinedSchedulesNotificationsChannel',
        group_ids: String(groupIds.join(',')),
        type,
        look_ahead: look_ahead || 0,
        start_time
      };

      actionCable = actionCable || getActionCable();
      return actionCable.subscriptions.create(channelData, listener);
    };

    // Get the initial information via the API.
    initialLoad();
    // Subscribe to websocket for incremental updates
    return subscribeToWebsocket();
  };

export const updateIntervalsInMemory =
  (id, type, intervals, startTime = null, endTime = null, clearGaps = true) =>
  dispatch => {
    const update_interval_start = startTime || intervals[0].start_time;
    const update_interval_end = endTime || intervals.at(-1).end_time;

    // optimistically update redux with the intervals that are known
    dispatch(
      actions.combinedScheduleIntervalsUpdatedOptimistically(
        id,
        type,
        intervals,
        update_interval_start,
        update_interval_end,
        { clearGaps }
      )
    );
  };

export const updateIntervalsInBackend =
  ({ startTime = null, endTime = null, ignoreWarnings = true }) =>
  (dispatch, getState) => {
    // For each transient schedule make an API call to update in database
    const promises = transientSchedules(getState()).map(schedule => {
      const transientIntervals = schedule.intervals.filter(i => i.transient);
      const url = `/api/v2/${schedule.owner_type}s/${schedule.owner_id}/combined_schedule`;
      const update_interval_start = startTime || transientIntervals[0].start_time;
      const update_interval_end = endTime || transientIntervals.at(-1).end_time;

      // return all intervals in the update period, not only the transient ones.
      // If not, any intervals in between which are not transient will be deleted
      //
      // If, for example, we have the following situation:
      // <=========A*=======><=======B=======><=======C*======>
      // And `A*` and `C*` are the only changed intervals, the following schedule
      // fragment would be sent to the server:
      // <=========A*=======>                 <=======C*======>
      // Causing interval B to be deleted.
      const intervals = overlappingIntervals(schedule.intervals, update_interval_start, update_interval_end);

      let data = {
        update_interval_start,
        update_interval_end,
        update_supplied_interval_attributes_only: true,
        // allow creating intervals with availability codes
        // that were created entirely in memory.
        apply_to_existing_intervals_only: false,
        intervals,
        ignore_schedule_warnings: ignoreWarnings
      };

      return axios.put(url, { ...data }).then(response => {
        dispatch(actions.combinedScheduleUpdated(schedule.owner_id, schedule.owner_type, response.data));
      });
    });

    return Promise.all(promises);
  };

export const clearScheduleWarnings = () => dispatch => {
  dispatch(actions.scheduleWarningsCleared());
};
